import { Injectable, signal } from '@angular/core';

/**
 * Servicio para almacenar y recuperar datos dinámicos extraídos del HTML del vehículo.
 * Similar a data-ap-shared pero para datos estructurados que pueden variar según el programa/vehículo.
 *
 * Los datos se almacenan en memoria cuando el usuario hace clic en "Reservar" y están disponibles
 * para todos los componentes del proceso de reserva.
 */
@Injectable({
  providedIn: 'root'
})
export class VehicleTemplateDataService {

  /**
   * Almacena los datos extraídos del HTML del vehículo.
   * Estructura flexible: { [key: string]: any }
   * Ejemplo: { pretitle: '100% eléctrico', subtitle: 'SUV INNOVADOR Y POTENTE', specs: {...} }
   */
  private vehicleTemplateData = signal<Record<string, any> | null>(null);

  /**
   * ID del vehículo actual cuyos datos están almacenados
   */
  private currentVehicleId = signal<string | null>(null);

  /**
   * Clave para almacenar en sessionStorage
   */
  private readonly STORAGE_KEY = 'vehicle_template_data';
  private readonly STORAGE_VEHICLE_ID_KEY = 'vehicle_template_data_vehicle_id';

  constructor() {
    // Cargar datos desde sessionStorage al inicializar el servicio
    this.loadFromStorage();
  }

  /**
   * Almacena los datos extraídos del HTML del vehículo
   * @param vehicleId ID del vehículo
   * @param data Datos extraídos del HTML (estructura flexible)
   */
  setVehicleData(vehicleId: string, data: Record<string, any>): void {
    this.currentVehicleId.set(vehicleId);
    this.vehicleTemplateData.set(data);
    // Persistir en sessionStorage
    this.saveToStorage(vehicleId, data);
  }

  /**
   * Obtiene todos los datos almacenados del vehículo actual
   * @returns Datos del vehículo o null si no hay datos almacenados
   */
  getVehicleData(): Record<string, any> | null {
    return this.vehicleTemplateData();
  }

  /**
   * Obtiene un dato específico por su clave
   * @param key Clave del dato a recuperar
   * @returns Valor del dato o null si no existe
   */
  getData(key: string): any {
    const data = this.vehicleTemplateData();
    return data ? data[key] : null;
  }

  /**
   * Obtiene un dato específico con valor por defecto si no existe
   * @param key Clave del dato a recuperar
   * @param defaultValue Valor por defecto si no existe
   * @returns Valor del dato o el valor por defecto
   */
  getDataOrDefault<T>(key: string, defaultValue: T): T {
    const data = this.vehicleTemplateData();
    return data && data[key] !== undefined ? data[key] : defaultValue;
  }

  /**
   * Verifica si hay datos almacenados para el vehículo actual
   * @param vehicleId ID del vehículo a verificar (opcional, usa el actual si no se proporciona)
   * @returns true si hay datos almacenados para el vehículo
   */
  hasVehicleData(vehicleId?: string): boolean {
    if (vehicleId) {
      return this.currentVehicleId() === vehicleId && this.vehicleTemplateData() !== null;
    }
    return this.vehicleTemplateData() !== null;
  }

  /**
   * Obtiene el ID del vehículo actual
   * @returns ID del vehículo o null
   */
  getCurrentVehicleId(): string | null {
    return this.currentVehicleId();
  }

  /**
   * Limpia los datos almacenados (útil al finalizar o cancelar la reserva)
   */
  clearVehicleData(): void {
    this.currentVehicleId.set(null);
    this.vehicleTemplateData.set(null);
    // Limpiar también sessionStorage
    this.clearStorage();
  }

  /**
   * Obtiene un signal de solo lectura para observar cambios en los datos
   * Útil para usar con computed() o efectos reactivos
   */
  getVehicleDataSignal() {
    return this.vehicleTemplateData.asReadonly();
  }

  /**
   * Extrae datos marcados con atributos data-ap-vehicle-data del HTML proporcionado.
   * Similar a data-ap-shared pero para extraer datos estructurados en lugar de elementos HTML.
   *
   * Los elementos deben tener el atributo data-ap-vehicle-data con el nombre de la clave,
   * y opcionalmente data-ap-vehicle-data-type para especificar el tipo de extracción:
   * - 'text': Extrae el texto del elemento (innerText)
   * - 'html': Extrae el HTML del elemento (innerHTML)
   * - 'attribute': Extrae el valor de un atributo específico (requiere data-ap-vehicle-data-attr)
   * - 'json': Parsea el contenido como JSON
   *
   * Ejemplo de uso en HTML:
   * <div data-ap-vehicle-data="pretitle" data-ap-vehicle-data-type="text">100% eléctrico</div>
   * <div data-ap-vehicle-data="subtitle" data-ap-vehicle-data-type="text">SUV INNOVADOR Y POTENTE</div>
   * <div data-ap-vehicle-data="specs" data-ap-vehicle-data-type="json">{"power": "299 CV", "range": "520 KM"}</div>
   *
   * @param htmlString HTML del vehículo del cual extraer los datos
   * @returns Objeto con los datos extraídos: { [key: string]: any } o null si no se encontraron datos
   */
  extractVehicleData(htmlString: string): Record<string, any> | null {
    const data: Record<string, any> = {};

    // Crear un elemento temporal para parsear el HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;

    // Buscar todos los elementos con data-ap-vehicle-data
    const dataElements = tempDiv.querySelectorAll('[data-ap-vehicle-data]');

    // Si no hay elementos con data-ap-vehicle-data, retornar null
    if (dataElements.length === 0) {
      console.warn('No se encontraron elementos con data-ap-vehicle-data en el template del vehículo');
      return null;
    }

    dataElements.forEach(element => {
      const key = element.getAttribute('data-ap-vehicle-data');
      if (!key) return;

      const dataType = element.getAttribute('data-ap-vehicle-data-type') || 'text';

      let value: any;

      switch (dataType) {
        case 'text':
          value = element.textContent?.trim() || '';
          break;

        case 'html':
          value = element.innerHTML.trim();
          break;

        case 'attribute':
          const attrName = element.getAttribute('data-ap-vehicle-data-attr');
          if (attrName) {
            value = element.getAttribute(attrName) || '';
          } else {
            value = '';
          }
          break;

        case 'json':
          try {
            value = JSON.parse(element.textContent?.trim() || '{}');
          } catch (e) {
            console.warn(`Error parsing JSON for key "${key}":`, e);
            value = {};
          }
          break;

        default:
          value = element.textContent?.trim() || '';
      }

      // Si la clave contiene puntos, crear estructura anidada
      // Ejemplo: "specs.power" -> { specs: { power: value } }
      if (key.includes('.')) {
        const keys = key.split('.');
        let current = data;
        for (let i = 0; i < keys.length - 1; i++) {
          if (!current[keys[i]]) {
            current[keys[i]] = {};
          }
          current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;
      } else {
        data[key] = value;
      }
    });

    // Verificar si el objeto tiene al menos una propiedad con valor no vacío
    const hasValidData = Object.keys(data).length > 0 &&
      Object.values(data).some(value => {
        // Considerar válido si no es null, undefined, string vacío u objeto vacío
        if (value === null || value === undefined || value === '') return false;
        if (typeof value === 'object' && Object.keys(value).length === 0) return false;
        return true;
      });

    return hasValidData ? data : null;
  }

  /**
   * Extrae y almacena los datos del vehículo en un solo paso
   * @param vehicleId ID del vehículo
   * @param htmlString HTML del vehículo del cual extraer los datos
   * @returns true si se extrajeron y almacenaron datos, false si no se encontraron datos en el template
   */
  extractAndSetVehicleData(vehicleId: string, htmlString: string): boolean {
    const data = this.extractVehicleData(htmlString);
    if (data) {
      this.setVehicleData(vehicleId, data);
      return true;
    }
    // Si no hay datos, no almacenar nada (mantener el estado anterior o null)
    console.warn(`No se pudieron extraer datos del template para el vehículo ${vehicleId}`);
    return false;
  }

  /**
   * Verifica si hay datos disponibles y si no, intenta recargarlos desde el HTML proporcionado
   * @param vehicleId ID del vehículo
   * @param htmlString HTML del vehículo del cual extraer los datos (opcional, solo si no hay datos)
   * @returns true si hay datos disponibles (ya existían o se recargaron), false si no se pudieron obtener
   */
  ensureVehicleData(vehicleId: string, htmlString?: string): boolean {
    // Verificar si ya hay datos para este vehículo
    if (this.hasVehicleData(vehicleId)) {
      return true;
    }

    // Si se proporciona HTML, intentar extraer los datos
    if (htmlString) {
      return this.extractAndSetVehicleData(vehicleId, htmlString);
    }

    return false;
  }

  /**
   * Guarda los datos en sessionStorage
   */
  private saveToStorage(vehicleId: string, data: Record<string, any>): void {
    try {
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
      sessionStorage.setItem(this.STORAGE_VEHICLE_ID_KEY, vehicleId);
    } catch (e) {
      console.warn('Error guardando datos del vehículo en sessionStorage:', e);
    }
  }

  /**
   * Carga los datos desde sessionStorage
   */
  private loadFromStorage(): void {
    try {
      const storedData = sessionStorage.getItem(this.STORAGE_KEY);
      const storedVehicleId = sessionStorage.getItem(this.STORAGE_VEHICLE_ID_KEY);

      if (storedData && storedVehicleId) {
        const data = JSON.parse(storedData);
        this.currentVehicleId.set(storedVehicleId);
        this.vehicleTemplateData.set(data);
      }
    } catch (e) {
      console.warn('Error cargando datos del vehículo desde sessionStorage:', e);
      // Limpiar datos corruptos
      this.clearStorage();
    }
  }

  /**
   * Limpia los datos de sessionStorage
   */
  private clearStorage(): void {
    try {
      sessionStorage.removeItem(this.STORAGE_KEY);
      sessionStorage.removeItem(this.STORAGE_VEHICLE_ID_KEY);
    } catch (e) {
      console.warn('Error limpiando sessionStorage:', e);
    }
  }
}
